import React, { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useMonths } from "../hooks/useMonths";
import { Company } from "../api/models/companies.models";
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";

const FilterContainer = styled.div`
  margin-bottom: 2rem;
  margin-left: 1rem;
`;

const FilterToggle = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
  color: #333;
  font-weight: 500;
  max-width: fit-content;
  padding: 0.5rem 1rem;
  background-color: var(--sumi-blue);
  color: var(--sumi-text-primary);
  border-radius: var(--border-radius);
  margin-bottom: 1rem;

  &:hover {
    background-color: var(--sumi-darker-blue);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--sumi-darker-blue);
  }
`;

const ChevronIcon = styled.i<{ $isOpen: boolean }>`
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
`;

const StyledAutoComplete = styled(AutoComplete)`
  width: 100%;
  min-width: 350px;

  .p-autocomplete-input {
    width: 100%;
  }
`;

const AutoCompleteWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius);
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  i {
    font-size: 1rem;
  }
`;

const FiltersGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: #d3d3d3 1px solid;
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

interface AdminInvoiceFiltersProps {
  filters: InvoiceFilters;
  onFilterChange: (key: keyof InvoiceFilters, value: any) => void;
  companies?: Company[];
}

export interface InvoiceFilters {
  company?: Company;
  status?: number;
  year?: number;
  quarter?: number;
  month?: number;
  invoiceNumber?: string;
  skip?: number;
  take?: number;
}

const AdminInvoiceFilters: React.FC<AdminInvoiceFiltersProps> = ({
  filters,
  onFilterChange,
  companies,
}) => {
  const { t } = useTranslation();
  const { monthOptions } = useMonths();
  const [isOpen, setIsOpen] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);

  const businessIdentifierId = 50;

  const statusOptions = [
    { label: t("status-unpaid"), value: 0 },
    { label: t("status-paid"), value: 1 },
    { label: t("status-overdue"), value: 2 },
  ];

  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { label: year.toString(), value: year };
  });

  const quarterOptions = [
    { label: "Q1", value: 1 },
    { label: "Q2", value: 2 },
    { label: "Q3", value: 3 },
    { label: "Q4", value: 4 },
  ];

  const search = (event: AutoCompleteCompleteEvent) => {
    setTimeout(() => {
      let _filteredCompanies;

      if (!event.query.trim().length) {
        _filteredCompanies = [...(companies || [])];
      } else {
        _filteredCompanies = companies?.filter((company) => {
          return (
            company.companyName
              .toLowerCase()
              .includes(event.query.toLowerCase()) ||
            company.identifiers
              .find((id) => id.identifierType.id === 50)
              ?.text?.toLowerCase()
              .includes(event.query.toLowerCase())
          );
        });
      }

      _filteredCompanies = _filteredCompanies?.sort((a, b) =>
        a.companyName.localeCompare(b.companyName, undefined, {
          sensitivity: "base",
        }),
      );

      setFilteredCompanies(_filteredCompanies);
    }, 250);
  };

  const handleCompanyChange = (e: { value: Company | null }) => {
    const company = e.value;
    setSelectedCompany(company);
    onFilterChange("company", company || undefined);
  };

  useEffect(() => {
    if (filters.company && companies && companies.length > 0) {
      const company = companies.find((c) => c.id === filters.company?.id);
      if (company) {
        setSelectedCompany(company);
      }
    } else if (!filters.company) {
      setSelectedCompany(null);
    }
  }, [filters.company, companies]);

  const handleRemoveCompany = () => {
    setSelectedCompany(null);
    onFilterChange("company", undefined);
  };

  return (
    <FilterContainer>
      <FilterToggle
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={isOpen}
      >
        <span>{t("label-filters")}</span>
        <ChevronIcon className="pi pi-chevron-down" $isOpen={isOpen} />
      </FilterToggle>

      {isOpen && (
        <FiltersGrid>
          <FilterRow>
            <div>
              <label
                htmlFor="company"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("label-active-company")}
              </label>
              <AutoCompleteWrapper>
                <StyledAutoComplete
                  id="company"
                  value={filters.company}
                  suggestions={filteredCompanies}
                  dropdown
                  completeMethod={search}
                  field="companyName"
                  onChange={handleCompanyChange}
                  placeholder={t("placeholder-select-company")!}
                  itemTemplate={(company: Company) => {
                    const businessId = company.identifiers.find(
                      (id) => id.identifierType.id === businessIdentifierId,
                    )?.text;
                    return businessId
                      ? `${company.companyName} - ${businessId}`
                      : company.companyName;
                  }}
                  selectedItemTemplate={(company: Company) => {
                    const businessId = company.identifiers.find(
                      (id) => id.identifierType.id === businessIdentifierId,
                    )?.text;
                    return businessId
                      ? `${company.companyName} - ${businessId}`
                      : company.companyName;
                  }}
                />
                <RemoveButton
                  onClick={handleRemoveCompany}
                  disabled={!selectedCompany}
                  title={t("label-clear")}
                >
                  <i className="pi pi-times" />
                </RemoveButton>
              </AutoCompleteWrapper>
            </div>

            <div>
              <label
                htmlFor="status"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("header-status")}
              </label>
              <Dropdown
                id="status"
                value={filters.status}
                options={statusOptions}
                onChange={(e) => onFilterChange("status", e.value)}
                placeholder={t("placeholder-select-status")}
                style={{ width: "100%" }}
                showClear
              />
            </div>

            <div>
              <label
                htmlFor="invoiceNumber"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("header-invoice-number")}
              </label>
              <InputText
                id="invoiceNumber"
                value={filters.invoiceNumber || ""}
                onChange={(e) =>
                  onFilterChange("invoiceNumber", e.target.value)
                }
                placeholder={t("placeholder-invoice-number")}
                style={{ width: "100%" }}
              />
            </div>
          </FilterRow>

          <FilterRow>
            <div>
              <label
                htmlFor="year"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("label-year")}
              </label>
              <Dropdown
                id="year"
                value={filters.year}
                options={yearOptions}
                onChange={(e) => onFilterChange("year", e.value)}
                placeholder={t("placeholder-select-year")}
                style={{ width: "100%" }}
                showClear
              />
            </div>

            <div>
              <label
                htmlFor="quarter"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("label-quarter")}
              </label>
              <Dropdown
                id="quarter"
                value={filters.quarter}
                options={quarterOptions}
                onChange={(e) => onFilterChange("quarter", e.value)}
                placeholder={t("placeholder-select-quarter")}
                style={{ width: "100%" }}
                showClear
              />
            </div>

            <div>
              <label
                htmlFor="month"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {t("label-month")}
              </label>
              <Dropdown
                id="month"
                value={filters.month}
                options={monthOptions}
                onChange={(e) => onFilterChange("month", e.value)}
                placeholder={t("placeholder-select-month")}
                style={{ width: "100%" }}
                showClear
              />
            </div>
          </FilterRow>
        </FiltersGrid>
      )}
    </FilterContainer>
  );
};

export default AdminInvoiceFilters;
